import React, { useState, useEffect } from "react";
import { Plus, X } from "lucide-react";
import { datamapIndicatorsApi, indicatorsApi } from "../../utils/api";
import { PrimaryButton, SecondaryButton } from "../common/ActionButtons";

const DataMapIndicators = ({ datamapId, isOpen, onClose }) => {
  const [associatedIndicators, setAssociatedIndicators] = useState([]);
  const [availableIndicators, setAvailableIndicators] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [selectedIndicatorId, setSelectedIndicatorId] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");

  useEffect(() => {
    if (isOpen && datamapId) {
      fetchAssociatedIndicators();
      fetchAvailableIndicators();
      fetchCategories();
    }
  }, [isOpen, datamapId]);

  const fetchAssociatedIndicators = async () => {
    try {
      const data = await datamapIndicatorsApi.getByDatamap(datamapId);
      setAssociatedIndicators(data || []);
    } catch (error) {
      console.error("Error fetching associated indicators:", error);
      setAssociatedIndicators([]);
    }
  };

  const fetchAvailableIndicators = async () => {
    try {
      const data = await indicatorsApi.getAll();
      setAvailableIndicators(data || []);
    } catch (error) {
      console.error("Error fetching available indicators:", error);
      setAvailableIndicators([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await indicatorsApi.getCategories();
      setCategoriesData(data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategoriesData([]);
    }
  };

  const handleAddIndicator = async (e) => {
    e.preventDefault();
    if (!selectedIndicatorId) return;

    setLoading(true);
    try {
      await datamapIndicatorsApi.create({
        dataMapID: datamapId,
        dataIndicatorID: selectedIndicatorId,
      });
      setSelectedIndicatorId("");
      fetchAssociatedIndicators();
    } catch (error) {
      console.error("Error adding indicator:", error);
      alert(
        "Error adding indicator. It may already be associated with this DataMap.",
      );
    }
    setLoading(false);
  };

  const handleRemoveIndicator = async (associationId) => {
    if (!window.confirm("Remove this indicator from the DataMap?")) return;

    setLoading(true);
    try {
      await datamapIndicatorsApi.delete(associationId);
      fetchAssociatedIndicators();
    } catch (error) {
      console.error("Error removing indicator:", error);
      alert("Error removing indicator.");
    }
    setLoading(false);
  };

  // Handle category change - reset subcategory when category changes
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setSelectedSubCategory(""); // Reset subcategory when category changes
  };

  // Get distinct categories from categories API data
  const availableCategories = [
    ...new Set(
      categoriesData
        .map((item) => item.category)
        .filter((cat) => cat && cat.trim() !== ""),
    ),
  ].sort();

  // Get distinct subcategories for the selected category from categories API data
  const availableSubCategories = selectedCategory
    ? [
        ...new Set(
          categoriesData
            .filter((item) => item.category === selectedCategory)
            .map((item) => item.subcategory)
            .filter((subcat) => subcat && subcat.trim() !== ""),
        ),
      ].sort()
    : [];

  // Filter out already associated indicators
  let unassociatedIndicators = availableIndicators.filter(
    (ind) =>
      !associatedIndicators.some(
        (assoc) =>
          assoc.dataindicatorid === ind.id || assoc.dataIndicatorID === ind.id,
      ),
  );

  // Apply category filter if selected
  if (selectedCategory) {
    unassociatedIndicators = unassociatedIndicators.filter(
      (ind) => ind.category === selectedCategory,
    );
  }

  // Apply subcategory filter if selected
  if (selectedSubCategory) {
    unassociatedIndicators = unassociatedIndicators.filter(
      (ind) => ind.subcategory === selectedSubCategory,
    );
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">
            Manage Data Indicators
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Add Indicator Form */}
          <div className="mb-6 bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h4 className="text-sm font-medium text-slate-700 mb-3">
              Add Indicator to DataMap
            </h4>

            {/* Filter Controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Filter by Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Categories</option>
                  {availableCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Filter by Subcategory
                </label>
                <select
                  value={selectedSubCategory}
                  onChange={(e) => setSelectedSubCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={!selectedCategory}
                >
                  <option value="">All Subcategories</option>
                  {availableSubCategories.map((subcategory) => (
                    <option key={subcategory} value={subcategory}>
                      {subcategory}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <form onSubmit={handleAddIndicator} className="flex gap-3">
              <select
                value={selectedIndicatorId}
                onChange={(e) => setSelectedIndicatorId(e.target.value)}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select an indicator...</option>
                {unassociatedIndicators.map((indicator) => (
                  <option key={indicator.id} value={indicator.id}>
                    ({indicator.dataindicator}) - {indicator.source}
                  </option>
                ))}
              </select>
              <PrimaryButton
                type="submit"
                disabled={loading || !selectedIndicatorId}
                icon={Plus}
              >
                Add
              </PrimaryButton>
            </form>
          </div>

          {/* Associated Indicators List */}
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-3">
              Associated Indicators ({associatedIndicators.length})
            </h4>
            {associatedIndicators.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No indicators associated with this DataMap yet.
              </div>
            ) : (
              <div className="space-y-2">
                {associatedIndicators.map((assoc) => {
                  // Find the full indicator details
                  const indicator = availableIndicators.find(
                    (ind) =>
                      ind.id ===
                      (assoc.dataindicatorid || assoc.dataIndicatorID),
                  );

                  return (
                    <div
                      key={assoc.id}
                      className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">
                          {indicator?.dataindicator || "Unknown Indicator"} -{" "}
                          {indicator?.source || "N/A"}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveIndicator(assoc.id)}
                        disabled={loading}
                        className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Remove indicator"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end">
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
        </div>
      </div>
    </div>
  );
};

export default DataMapIndicators;
